import { useTranslate } from 'core/hooks/useTranslate';
import Icon from 'core/modules/atoms/Icon';
import PropTypes from 'prop-types';
import React from 'react';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning.svg';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import './ToolIndicator.scss';

/**
 * Generates a deterministic hash-based index from a string
 */
function generateHashIndex(str, arrayLength) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % arrayLength;
}

/**
 * Displays a visual indicator for active tool events
 * Shows loading state while tools are processing
 */
function ToolIndicator({ toolEvent }) {
  const { t } = useTranslate(['chatbot']);

  const getToolIcon = (toolType) => {
    switch (toolType) {
      case 'd3_visualization':
        return 'magic-wand';
      case 'mermaid_diagram':
        return 'magic-wand';
      case 'content_retrieval':
        return 'magic-wand';
      case 'canvas_generation':
        return 'magic-wand';
      default:
        return 'magic-wand';
    }
  };

  const getToolMessage = (toolType, status, timestamp) => {
    if (status === 'failed') {
      return t('chatbot:Error processing tool');
    }

    // Array de mensajes aleatorios disponibles
    const randomMessages = [
      'Creating something great for you',
      'Preparing something useful for you',
      'This will help, give me a moment',
      'Your resource is on its way',
    ];

    // Generar índice determinístico basado en timestamp
    const index = generateHashIndex(timestamp, randomMessages.length);

    return t(`chatbot:${randomMessages[index]}`);
  };

  const isActive = toolEvent.status === 'started';
  const isFailed = toolEvent.status === 'failed';

  return (
    <div className={`tool-indicator ${isFailed ? 'tool-indicator--error' : isActive ? 'tool-indicator--active' : ''}`}>
      <div className="tool-indicator__icon">
        {isFailed ? <WarningIcon className="chat-message__error-icon-image" /> : <Icon type={getToolIcon(toolEvent.toolType)} size="small" />}
      </div>
      <div className="tool-indicator__content">
        <span className="tool-indicator__message">{getToolMessage(toolEvent.toolType, toolEvent.status, toolEvent.timestamp)}</span>
        {isActive && <TypingIndicator />}
      </div>
    </div>
  );
}

ToolIndicator.propTypes = {
  toolEvent: PropTypes.shape({
    type: PropTypes.oneOf(['tool_invocation', 'tool_completion']).isRequired,
    toolName: PropTypes.string.isRequired,
    toolType: PropTypes.oneOf(['d3_visualization', 'mermaid_diagram', 'content_retrieval', 'threejs_visualization', 'canvas_generation', 'unknown']).isRequired,
    status: PropTypes.oneOf(['started', 'completed', 'failed']).isRequired,
    timestamp: PropTypes.string.isRequired,
    args: PropTypes.any,
    result: PropTypes.any,
    id: PropTypes.string,
  }).isRequired,
};

export default ToolIndicator;
